import { useLayoutEffect, type RefObject } from 'react';
import { chatCeilingFor } from './keyboard.js';

/**
 * Keeping the composer above the on-screen keyboard, and nothing else.
 *
 * **The height of the chat is not measured here, and that is deliberate.** The navigation
 * shell gives this screen a box of the right height in CSS alone: `.shell` is
 * `min-height: calc(100dvh - the safe-area insets)`, `main.stage` is a flex column inside
 * it, and the chat's root carries `fills`, which is `flex: 1 1 auto; min-height: 0`. Flex
 * arithmetic does the rest, with no JavaScript and nothing to keep in step with the height
 * of a header this file does not own. That is a better mechanism than measuring and it is
 * used wherever it works.
 *
 * It does not work for the keyboard, and the keyboard is the whole of what is left.
 *
 * **`dvh` does not notice a keyboard.** It is easy to assume otherwise — the unit is
 * "dynamic viewport height", and it *is* dynamic — but what it tracks is browser chrome
 * collapsing, the address bar sliding away as you scroll. A software keyboard is not part
 * of that. On iOS the keyboard does not resize the layout viewport at all, so `dvh`, `svh`,
 * `lvh` and `vh` are one number before and after it opens; in a standalone app, with no
 * chrome to collapse, they were already one number. On Android the default
 * `interactive-widget=resizes-visual` says the same. So a layout sized purely in `dvh` puts
 * the composer behind the keyboard, on the one screen where typing is the entire point.
 *
 * `visualViewport` is the only API that reports it. Readings taken from the installed app on
 * the handset, composer focused and keyboard open, settle what the alternatives do:
 *
 * ```
 * standalone true   innerHeight 956   100dvh 956   visual.h 543   visual.top 0
 * ```
 *
 * **`innerHeight` and `100dvh` are the same number before and after the keyboard opens**, so
 * every derivation from either is dead on arrival — which is what three shipped fixes were.
 * `visual.h` is the only reading that moves.
 *
 * What is published is therefore **the height this element may not exceed**, measured
 * directly, and the stylesheet applies it as a `max-height`. That last part is not a detail:
 * the fourth reading in the same capture was `--chat-view-h 413px` against an element that
 * measured 776px tall. The number had been right all along and `height` could not enforce it,
 * because this element carries `fills` — `flex: 1 1 auto` — and `flex-grow` re-inflated it to
 * fill the shell's 956px column. `height` sets a flex *base*; `max-height` clamps after the
 * growing is done. See `chat.css`.
 *
 * Also rejected: `position: fixed` on the composer, which is what every chat on the web
 * tries first and is specifically broken on iOS — while the keyboard animates, fixed
 * elements are laid out against the layout viewport and end up floating mid-screen or
 * sliding behind it, and the focus scroll moves them again afterwards. The composer is in
 * normal flow as the last row of a flex column, which is the one layout iOS does not
 * second-guess. And `interactive-widget=resizes-content`, which would make Android do this
 * in CSS: it is a change to the viewport meta in `index.html`, which is every screen in the
 * app rather than this one, and iOS ignores it — so it buys one platform's half of a
 * behaviour this already has on both.
 */

/**
 * Publishes the keyboard's height on an element, and keeps the page behind it still.
 *
 * Two jobs, and they are the same job: a screen that fits the viewport exactly is worthless
 * if the document can scroll behind it. `overflow: hidden` on the body — via a class, so the
 * rule lives in this feature's own stylesheet rather than as an inline style fighting
 * whatever the shell sets — is what stops the whole app rubber-banding under a finger that
 * meant to scroll the thread. On iOS standalone that failure means the shell's fixed top bar
 * sliding out from under the status bar with no scrollbar and no way to know what happened.
 *
 * A layout effect rather than an effect, so the first measurement lands before the browser
 * paints. It is a no-op in the ordinary case — there is no keyboard when a screen mounts —
 * but the same is not true when this screen is remounted by a navigation while a field is
 * focused, and a frame of the composer behind the keyboard is exactly the kind of thing that
 * gets reported as "it jumps".
 */
export function useOwnedViewport(ref: RefObject<HTMLElement | null>): void {
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;

    const body = document.body;
    body.classList.add('chat-owns-viewport');

    let frame = 0;

    const measure = () => {
      frame = 0;
      const view = window.visualViewport;
      if (!view) return;

      /*
       * The height is measured against the visible window, not inferred from `innerHeight`.
       *
       * **Two attempts derived it and both were wrong on a real iPhone**, so this stops
       * deriving. The arithmetic was `innerHeight - visualHeight - offsetTop`, which is
       * correct *if* iOS keeps the layout viewport at full height while the keyboard is up.
       * When it does not — and on iOS standalone it sometimes does not — that expression
       * collapses to `-offsetTop`, lands under the 60px floor, and reports **no keyboard at
       * all**: no margin, composer behind the keys, which is exactly what was reported twice.
       * A second attempt then translated the screen down by `offsetTop` as well, which
       * double-counted it whenever the first reading had been right.
       *
       * `getBoundingClientRect()` and `visualViewport` are in the same coordinate frame, so
       * the visible window is `[offsetTop, offsetTop + height]` and the answer is simply the
       * distance from this element's top edge to the bottom of that window. It needs no
       * assumption about what iOS does to the layout viewport, which is the only assumption
       * that has been wrong here.
       *
       * It measures in JavaScript, which the first design set out to avoid and was right to
       * prefer. But the thing being measured is a number no stylesheet can express, and a
       * principle that produces a composer nobody can see is not worth keeping.
       */
      const rect = element.getBoundingClientRect();
      const height = chatCeilingFor(
        { innerHeight: window.innerHeight, height: view.height, offsetTop: view.offsetTop },
        rect.top,
      );

      if (height === undefined) {
        element.style.removeProperty('--chat-view-h');
        const idle = document.scrollingElement;
        if (idle && idle.scrollTop !== 0) idle.scrollTop = 0;
        return;
      }

      const current = Number.parseFloat(element.style.getPropertyValue('--chat-view-h')) || 0;
      if (Math.abs(current - height) > 1) {
        element.style.setProperty('--chat-view-h', `${height}px`);
      }

      /*
       * The *document* scroll is put back; the *visual viewport* offset is not fought.
       *
       * These are different numbers and conflating them is what made the screen bounce.
       * `scrollingElement.scrollTop` is the document scrolling under a locked body, and zero
       * is the only legitimate value. `visualViewport.offsetTop` is iOS moving the visible
       * window, **nothing on the page can set it**, and it is now simply part of the
       * measurement above rather than something to correct.
       */
      const scroller = document.scrollingElement;
      if (scroller && scroller.scrollTop !== 0) scroller.scrollTop = 0;
    };

    /*
     * Coalesced into one frame. iOS fires `resize` on the visual viewport for every step of
     * the keyboard animation — dozens in a few hundred milliseconds — and writing a custom
     * property on each one invalidates layout that many times, which is precisely how a
     * keyboard opening becomes a stutter.
     */
    const schedule = () => {
      if (frame !== 0) return;
      frame = requestAnimationFrame(measure);
    };

    measure();

    const view = window.visualViewport;
    view?.addEventListener('resize', schedule);
    view?.addEventListener('scroll', schedule);
    window.addEventListener('resize', schedule);
    window.addEventListener('orientationchange', schedule);

    return () => {
      if (frame !== 0) cancelAnimationFrame(frame);
      view?.removeEventListener('resize', schedule);
      view?.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      window.removeEventListener('orientationchange', schedule);
      body.classList.remove('chat-owns-viewport');
      element.style.removeProperty('--chat-view-h');
    };
  }, [ref]);
}
